import { MUTATION_LEDGER_VERSION } from './mutation-ledger/schemas';
import {
  OBJECTTYPES,
  TpDataContext,
  TpDataTable,
  TpDataTableColumn,
  TpDataTableMeta,
  TpDataTableGroup,
  VALTYPES,
  MANIFEST_DATA_TYPE_TO_VALUE_TYPE,
} from './interfaces';
import {
  convertAllCategoricalColumns,
  loadDendrogram,
  makeIntegerColumn,
  makeMultiCategoricalColumn,
  makeNumericalColumn,
  makeStringColumn,
} from './init-data/helpers';
import { fetchBitmapDataObject } from './data-objects/bitmap';
import { DataContext } from '../api/models/data';
import { parse as yamlParse } from 'yaml';
import { getColumnInfo } from './analyticsDataSlice';
import { Ontology } from '../api/models/assets';
import { createEmptyEnSelectionFilter } from './selection/selectionEnFilters';
import { parseTsvFromStream } from './tsvParser';

export function createItemIdMap(itemIdList: string[]) {
  const rs: { [id: string]: number } = {};
  let idx = 0;
  for (const itemId of itemIdList) {
    rs[itemId] = idx;
    idx++;
  }
  return rs;
}

export function generateDataTable(
  columnIds: string[],
  columnValues: string[][],
  colItemId: string,
  rowDataType: string,
  colLabel: string,
  metadata: any,
  displayName: string,
  tableId: string,
): TpDataTable {
  let colIdx = 0; // set to 1 to skip the first column
  const columns: any[] = [];
  const columnData: any = {};
  for (const headerId of columnIds) {
    columnData[headerId] = columnValues[colIdx];
    const prop = metadata.properties.find((p: any) => p.identifier == headerId);
    const valueType = prop?.dataType || VALTYPES.STRING;
    columns.push({
      id: headerId,
      name: prop?.displayName || headerId,
      description: prop?.description,
      valueType,
      augmentedData: {},
      decimalDigits: 3,
      columnCanonicalTypeId: prop?.columnCanonicalTypeId,
      columnItemId: prop?.columnItemId,
      groupId: prop?.group,
    });
    colIdx += 1;
  }

  const columnGroups = metadata.propertyGroups
    ? metadata.propertyGroups.map((group: any) => ({
        name: group.displayName,
        id: group.identifier,
        description: group.description,
      }))
    : [];

  if (!columnData[colItemId]) throw `Invalid identifier column ${colItemId} for table ${tableId}`;
  const dataTable: TpDataTable = {
    columns,
    columnData,
    columnGroups,
    id: tableId,
    name: displayName || metadata.displayName,
    description: metadata.description,
    canonicalTypeId: rowDataType,
    colItemId,
    colLabel,
    itemIdMap: createItemIdMap(columnData[colItemId]),
    baseColumnIds: columns.map((column) => column.id),
    baseColumnGroupIds: columnGroups.map((group: TpDataTableGroup) => group.id),
  };

  if (metadata.relationsForEntity) {
    dataTable.relationsForEntity = metadata.relationsForEntity;
    dataTable.relationFrom = metadata.relationFrom;
    dataTable.relationTo = metadata.relationTo;
  }

  return dataTable;
}

function processDendrogramDataObject(dataObject: any, dataContext: TpDataContext, ontology: Ontology) {
  let metadataObj: any;
  const dendrogramFetch = fetch(dataObject.metadataUrl)
    .then((response) => response.text())
    .then((metadata) => {
      metadataObj = yamlParse(metadata);
      return fetch(dataObject.publicUrl);
    })
    .then((response) => response.text())
    .then((tsvDataStr) => {
      return loadDendrogram(
        dataObject.tableId,
        dataObject.displayName,
        metadataObj.description ?? '',
        metadataObj.tableEntityType,
        tsvDataStr,
      );
    });
  return dendrogramFetch;
}

async function processTableMeta(dataObject: any, dataContext: TpDataContext, ontology: Ontology) {
  const metaDataResponse = await fetch(dataObject.metadataUrl);
  const metaDataText = await metaDataResponse.text();
  const metadataObj = yamlParse(metaDataText);

  if (!Object.hasOwnProperty.call(dataContext.canonicalTypes, metadataObj.entityType)) {
    dataContext.canonicalTypes[metadataObj.entityType] = {
      id: metadataObj.entityType,
      name: ontology.entityTypes[metadataObj.entityType].displayNamePlural,
      itemIdList: [],
      selectedItemsMap: null,
      restrictionFilter: null,
      restrictedItemsMap: null,
      restrictionList: null,
      restrictedItemId2Inxdex: {},
      selectionFilter: createEmptyEnSelectionFilter(),
    };
  }
  return metadataObj;
}

async function processEntityTableDataObject(dataObject: any, dataContext: TpDataContext, ontology: Ontology) {
  const metadataObj: any = await processTableMeta(dataObject, dataContext, ontology);
  const columnSeparator = dataObject.location.endsWith('.tsv') ? '\t' : ',';
  const metaObjectFetch = parseTsvFromStream(dataObject.publicUrl, columnSeparator)
    .then(({ headers, columns }) => {
      return generateDataTable(
        headers,
        columns,
        ontology.entityTypes[dataObject.entityType].primaryKey,
        dataObject.entityType,
        'Identifier',
        metadataObj,
        dataObject.displayName,
        dataObject.tableId,
      );
    })
    .then((dataTable) => {
      for (const sampleProperty of metadataObj.properties) {
        makeColumn(dataTable, sampleProperty);
        const column = getColumnInfo(dataTable, sampleProperty.identifier);
        column.groupId = sampleProperty.group;
      }
      return dataTable;
    });
  return metaObjectFetch;
}

async function processLazyLoadingTableDataObject(
  dataObject: any,
  dataContext: TpDataContext,
  ontology: Ontology,
): Promise<TpDataTableMeta> {
  const metaDataResponse = await fetch(dataObject.metadataUrl);
  const metaDataText = await metaDataResponse.text();
  const metadataObj = yamlParse(metaDataText);
  const columns = [];

  for (const property of metadataObj.properties) {
    let valueType = property?.dataType || VALTYPES.STRING;
    valueType = MANIFEST_DATA_TYPE_TO_VALUE_TYPE[valueType] ?? valueType;
    columns.push({
      id: property.identifier,
      name: property.displayName || property.identifier,
      description: property.description,
      valueType,
      augmentedData: {},
      decimalDigits: property.decimalDigits ?? 3,
      columnCanonicalTypeId: property.columnCanonicalTypeId,
      columnItemId: property.columnItemId,
      groupId: property.group,
    });
  }

  const columnGroups =
    metadataObj.propertyGroups?.map((group: any) => {
      return {
        name: group.displayName,
        id: group.identifier,
        description: group.description,
      };
    }) ?? [];

  const dataTableMeta: TpDataTableMeta = {
    columns,
    columnGroups,
    id: dataObject.tableId,
    name: dataObject.displayName,
    description: metadataObj.description,
    canonicalTypeId: dataObject.entityType,
    colItemId: ontology.entityTypes[dataObject.entityType].primaryKey,
    colLabel: 'Identifier',
  };

  if (metadataObj.relationsForEntity) {
    dataTableMeta.relationsForEntity = metadataObj.relationsForEntity;
    dataTableMeta.relationFrom = metadataObj.relationFrom;
    dataTableMeta.relationTo = metadataObj.relationTo;
  }

  return dataTableMeta;
}

export function buildDataContext(dataContextResponse: DataContext, ontology: Ontology): Promise<TpDataContext> {
  const dataContext: TpDataContext = {
    assetId: dataContextResponse.assetId,
    id: dataContextResponse.id,
    name: dataContextResponse.displayName,
    dataTables: [],
    lazyLoadingTableMetas: [],
    bitmaps: {},
    dendrograms: [],
    canonicalTypes: {},
    mutationLedger: { version: MUTATION_LEDGER_VERSION, actions: [] },
  };
  const fetchBitmaps = [];
  const fetchDatatables = [];
  const fetchDendrograms = [];
  const fetchLazyLoadingTables: Promise<TpDataTableMeta>[] = [];
  const dataObjects = dataContextResponse.dataObjects;

  // fetch all the files from Google cloud storage
  for (const dataObject of dataObjects) {
    switch (dataObject.objectType) {
      case OBJECTTYPES.BITMAP: {
        const bitmapFetch = fetchBitmapDataObject(dataObject);
        fetchBitmaps.push(bitmapFetch);
        break;
      }
      case OBJECTTYPES.ENTITY_TABLE: {
        const metaObjectFetch = processEntityTableDataObject(dataObject, dataContext, ontology);
        fetchDatatables.push(metaObjectFetch);
        break;
      }
      case OBJECTTYPES.DENDROGRAM: {
        const dendrogramFetch = processDendrogramDataObject(dataObject, dataContext, ontology);
        fetchDendrograms.push(dendrogramFetch);
        break;
      }
      case OBJECTTYPES.LAZY_LOADING_TABLE: {
        const lazyLoadingTableMetaFetch = processLazyLoadingTableDataObject(dataObject, dataContext, ontology);
        fetchLazyLoadingTables.push(lazyLoadingTableMetaFetch);
        break;
      }
    }
  }

  return Promise.all(fetchBitmaps)
    .then((bitmaps) => {
      bitmaps.forEach((bitmap) => (dataContext.bitmaps[bitmap.id] = bitmap));
      return Promise.all(fetchDatatables);
    })
    .then((dataTables) => {
      dataContext.dataTables.push(...dataTables);
      for (const dataTable of dataContext.dataTables) {
        dataTable.baseColumnIds ??= dataTable.columns.map((column) => column.id);
        dataTable.baseColumnGroupIds ??= dataTable.columnGroups.map((group) => group.id);
      }
      convertAllCategoricalColumns(dataContext);
      return Promise.all(fetchLazyLoadingTables);
    })
    .then((lazyLoadingTableMetas) => {
      dataContext.lazyLoadingTableMetas.push(...lazyLoadingTableMetas);
      return Promise.all(fetchDendrograms);
    })
    .then((dendrogram) => {
      dataContext.dendrograms.push(...dendrogram);
      return dataContext;
    });
}

export function makeColumn(dataTable: TpDataTable, sampleProperty: any) {
  const dataType = sampleProperty.dataType;
  switch (dataType) {
    case VALTYPES.STRING:
      makeStringColumn(dataTable, sampleProperty.identifier);
      break;
    case VALTYPES.INTEGER:
      makeIntegerColumn(dataTable, sampleProperty.identifier);
      break;
    case VALTYPES.NUMBER:
      makeNumericalColumn(dataTable, sampleProperty.identifier, sampleProperty.decimalDigits);
      break;
    case VALTYPES.MULTISTRING:
      makeMultiCategoricalColumn(dataTable, sampleProperty.identifier);
      break;
    case VALTYPES.MULTICAT:
      makeMultiCategoricalColumn(dataTable, sampleProperty.identifier);
      break;
  }
}

export function computeColumn(
  dataTable: TpDataTable,
  colId: string,
  colName: string,
  dataType: string,
  groupId: string | null,
  // eslint-disable-next-line no-empty-pattern
  evalFunction: ({}) => any,
): TpDataTableColumn {
  const colData = [];
  for (let itemNr = 0; itemNr < dataTable.columnData[dataTable.colItemId].length; itemNr++) {
    const row: any = {};
    for (const column of dataTable.columns) row[column.id] = dataTable.columnData[column.id][itemNr];
    colData.push(evalFunction(row));
  }
  const colInfo = {
    id: colId,
    name: colName,
    groupId,
    valueType: dataType as any,
    augmentedData: {},
  };
  dataTable.columns.push(colInfo);
  dataTable.columnData[colId] = colData;
  return colInfo;
}
